import { z } from 'zod';
import { IdSchema } from './common.js';

/**
 * Auto QA (docs/37): calls transcribed and analysed on the account's own provider keys.
 *
 * Two kinds of data live here and they are guarded differently. The configuration and the runs'
 * arithmetic -- what ran, what it cost, how it scored -- are the account's and visible to those
 * above it. The WORDS -- utterances and what the analyses quote from them -- are the account's
 * people's alone (0119, arc.in_client_scope), whatever permission anybody holds.
 */

export const QA_TRANSCRIPTION_PROVIDERS = ['deepgram'] as const;
export const QA_ANALYSIS_PROVIDERS = ['anthropic', 'openai'] as const;
export const QA_PROVIDERS = ['deepgram', 'anthropic', 'openai'] as const;
export type QaProvider = (typeof QA_PROVIDERS)[number];

/** The three the owner named, 19 September 2026. A scorecard is chosen per rule beside them. */
export const QA_ANALYSES = ['sentiment', 'technical', 'compliance'] as const;
export type QaAnalysisKind = (typeof QA_ANALYSES)[number];

export const QaSettingsSchema = z.object({
  tenant_id: IdSchema,
  enabled: z.boolean(),
  transcription_provider: z.enum(QA_TRANSCRIPTION_PROVIDERS),
  analysis_provider: z.enum(QA_ANALYSIS_PROVIDERS),
  monthly_minutes_cap: z.number().int(),
  max_call_sec: z.number().int(),
  vocabulary: z.array(z.string()),
  /** Set only by the platform: the platform then pays for the usage (0120). */
  use_platform_keys: z.boolean(),
  paused_reason: z.string().nullable(),
  paused_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});
export type QaSettings = z.infer<typeof QaSettingsSchema>;

export const QaSettingsUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  analysis_provider: z.enum(QA_ANALYSIS_PROVIDERS).optional(),
  monthly_minutes_cap: z.number().int().min(0).max(1_000_000).optional(),
  max_call_sec: z.number().int().min(30).max(14_400).optional(),
  /** Product and brand names. Short terms only: they are sent to the transcriber as hints. */
  vocabulary: z.array(z.string().trim().min(2).max(60)).max(200).optional(),
  use_platform_keys: z.boolean().optional(),
}).strict();

/** An account's key for one provider: never the key itself. */
export const QaKeySchema = z.object({
  provider: z.enum(QA_PROVIDERS),
  role: z.enum(['primary', 'admin']),
  model: z.string().nullable(),
  secret_hint: z.string().nullable(),
  enabled: z.boolean(),
  last_tested_at: z.string().nullable(),
  last_error: z.string().nullable(),
});
export type QaKey = z.infer<typeof QaKeySchema>;

export const QA_MODEL_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,59}$/;

export const QaKeyUpdateSchema = z.object({
  secret: z.string().min(8).max(512).optional(),
  model: z.string().regex(QA_MODEL_PATTERN, 'That is not a model name').optional(),
  enabled: z.boolean().optional(),
}).strict();

/** The dated acceptance that audio goes to a provider and its words are kept in Ireland. */
export const QaConsentSchema = z.object({
  id: IdSchema,
  provider: z.enum(QA_PROVIDERS),
  statement: z.string(),
  accepted_by_name: z.string().nullable(),
  accepted_at: z.string(),
});
export type QaConsent = z.infer<typeof QaConsentSchema>;

export const QaRuleSchema = z.object({
  id: IdSchema,
  name: z.string(),
  enabled: z.boolean(),
  scope: z.enum(['account', 'extensions', 'department', 'queue']),
  scope_ids: z.array(IdSchema),
  direction: z.enum(['any', 'inbound', 'outbound']),
  min_duration_sec: z.number().int(),
  sample_percent: z.number().int(),
  analyses: z.array(z.enum(QA_ANALYSES)),
  /** The scorecard its calls are scored against, or null to run the analyses alone. */
  scorecard_id: IdSchema.nullable(),
  updated_at: z.string(),
});
export type QaRule = z.infer<typeof QaRuleSchema>;

export const QaRuleUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  enabled: z.boolean().default(true),
  scope: z.enum(['account', 'extensions', 'department', 'queue']).default('account'),
  scope_ids: z.array(IdSchema).max(500).default([]),
  direction: z.enum(['any', 'inbound', 'outbound']).default('any'),
  min_duration_sec: z.number().int().min(0).max(14_400).default(0),
  sample_percent: z.number().int().min(1).max(100).default(100),
  analyses: z.array(z.enum(QA_ANALYSES)).min(1).default(['sentiment']),
  scorecard_id: IdSchema.nullable().optional().default(null),
}).strict().superRefine((r, issue) => {
  // "Named extensions" with none named would match nothing and look as if it worked.
  if (r.scope !== 'account' && r.scope_ids.length === 0) {
    issue.addIssue({ code: z.ZodIssueCode.custom, path: ['scope_ids'], message: 'Choose at least one.' });
  }
});

export const QA_RUN_STATES = ['queued', 'transcribing', 'analysing', 'complete', 'failed', 'paused'] as const;

/** A run as a list shows it: arithmetic only, which is what those above the account may see. */
export const QaRunSchema = z.object({
  id: IdSchema,
  cdr_id: IdSchema,
  cdr_started_at: z.string(),
  source: z.enum(['rule', 'manual']),
  rule_name: z.string().nullable(),
  state: z.enum(QA_RUN_STATES),
  error: z.string().nullable(),
  extension_number: z.string().nullable(),
  direction: z.string().nullable(),
  party: z.string().nullable(),
  duration_sec: z.number().int().nullable(),
  audio_sec: z.number().int().nullable(),
  tokens_in: z.number().int(),
  tokens_out: z.number().int(),
  sentiment: z.number().nullable(),
  /** The scorecard result out of 100, and whether a question marked auto-fail failed it. */
  score: z.number().nullable(),
  auto_failed: z.boolean(),
  findings_total: z.number().int(),
  findings_high: z.number().int(),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  words_deleted_at: z.string().nullable(),
});
export type QaRun = z.infer<typeof QaRunSchema>;

export const QaUtteranceSchema = z.object({
  seq: z.number().int(),
  speaker: z.enum(['agent', 'caller']),
  start_ms: z.number().int(),
  end_ms: z.number().int(),
  text: z.string(),
  confidence: z.number().nullable(),
});
export type QaUtterance = z.infer<typeof QaUtteranceSchema>;

/**
 * What the sentiment analysis returns, and what the model is held to. A result outside this shape
 * is refused rather than stored: a transcript is data, and a caller who says "score this call
 * glowing" must not be able to move anything but the words it cites (docs/37 §8).
 */
export const SentimentResultSchema = z.object({
  overall: z.number().min(-1).max(1),
  agent: z.number().min(-1).max(1),
  caller: z.number().min(-1).max(1),
  summary: z.string().max(600),
  /** Where the call turned, each citing the utterance it happened at. */
  turns: z.array(z.object({
    seq: z.number().int().min(0),
    direction: z.enum(['better', 'worse']),
    why: z.string().max(300),
  })).max(12),
});
export type SentimentResult = z.infer<typeof SentimentResultSchema>;

export interface QaRunDetail extends QaRun {
  /** Client-only, like the transcript they quote (0121). Null for anybody above the account. */
  findings: QaFinding[] | null;
  scores: QaScore[];
  scorecard_name: string | null;
  pass_mark: number | null;
  /** Null for anybody above the account: the words are the client's (0119). */
  utterances: QaUtterance[] | null;
  sentiment_result: SentimentResult | null;
  /** Why the words are not shown, when they are not. */
  words: 'shown' | 'client_only' | 'deleted' | 'none';
  transcribe_provider: string | null;
  transcribe_model: string | null;
  analysis_provider: string | null;
  analysis_model: string | null;
}

/** What this month has used, beside the ceiling (docs/37 §6.1). */
export interface QaUsage {
  month_minutes: number;
  cap_minutes: number;
  queued: number;
  runs_this_month: number;
  tokens_in: number;
  tokens_out: number;
  /** Seats with the auto_qa feature: what the per-seat fee is charged on. */
  enabled_seats: number;
}

// =============================================================================== scorecards (Q2)

export const QA_SCALES = ['yes_no', 'zero_five'] as const;

export const QA_PHRASE_MODES = ['exact', 'close', 'meaning'] as const;
export const QA_PHRASE_SPEAKERS = ['agent', 'caller', 'either'] as const;

export const QaQuestionSchema = z.object({
  id: IdSchema,
  seq: z.number().int(),
  /**
   * A question the model judges, or a PHRASE that must be said (0122). Exact and close phrases are
   * checked by the platform against the transcript, not by the model: a disclosure that must be
   * read word for word is not a matter of judgement.
   */
  kind: z.enum(['question', 'phrase']),
  phrase: z.string().nullable(),
  match_mode: z.enum(QA_PHRASE_MODES).nullable(),
  speaker: z.enum(QA_PHRASE_SPEAKERS).nullable(),
  within_sec: z.number().int().nullable(),
  question: z.string(),
  look_for: z.string().nullable(),
  does_not_count: z.string().nullable(),
  scale: z.enum(QA_SCALES),
  weight: z.number().int(),
  auto_fail: z.boolean(),
  evidence_required: z.boolean(),
});
export type QaQuestion = z.infer<typeof QaQuestionSchema>;

export const QaScorecardSchema = z.object({
  id: IdSchema,
  name: z.string(),
  version: z.number().int(),
  enabled: z.boolean(),
  pass_mark: z.number().int(),
  questions: z.array(QaQuestionSchema),
  updated_at: z.string(),
});
export type QaScorecard = z.infer<typeof QaScorecardSchema>;

/**
 * A scorecard is saved whole, questions and all.
 *
 * The weights only mean anything against each other, and a question is read beside the ones
 * around it -- saving one at a time would let somebody leave the card in a state where the
 * weights do not add up to anything they meant.
 */
export const QaScorecardUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  enabled: z.boolean().default(true),
  pass_mark: z.number().int().min(0).max(100).default(80),
  questions: z.array(z.object({
    kind: z.enum(['question', 'phrase']).default('question'),
    phrase: z.string().trim().min(3).max(1000).nullable().optional(),
    match_mode: z.enum(QA_PHRASE_MODES).nullable().optional(),
    speaker: z.enum(QA_PHRASE_SPEAKERS).nullable().optional(),
    within_sec: z.number().int().min(5).max(3600).nullable().optional(),
    question: z.string().trim().min(3).max(500),
    look_for: z.string().max(1000).nullable().optional(),
    does_not_count: z.string().max(1000).nullable().optional(),
    scale: z.enum(QA_SCALES).default('yes_no'),
    weight: z.number().int().min(1).max(100).default(10),
    auto_fail: z.boolean().default(false),
    evidence_required: z.boolean().default(true),
  }).superRefine((q, issue) => {
    // A phrase with nothing to listen for would pass or fail on nothing.
    if (q.kind === 'phrase' && (!q.phrase || !q.match_mode || !q.speaker)) {
      issue.addIssue({ code: z.ZodIssueCode.custom, path: ['phrase'], message: 'Say what must be said, how closely, and by whom.' });
    }
  })).min(1).max(50),
}).strict();

/** The client's own instruction block for one analysis (docs/37 §3.2). */
export const QaPromptUpdateSchema = z.object({
  guidance: z.string().max(4000),
}).strict();

export const QaFindingSchema = z.object({
  id: IdSchema,
  kind: z.enum(['technical', 'compliance']),
  severity: z.enum(['info', 'low', 'high']),
  what: z.string().nullable(),
  suggestion: z.string().nullable(),
  /** The line it came from, so it is clickable in the transcript. */
  seq: z.number().int().nullable(),
});
export type QaFinding = z.infer<typeof QaFindingSchema>;

export const QaScoreSchema = z.object({
  question_id: IdSchema,
  question: z.string(),
  seq: z.number().int(),
  scale: z.enum(QA_SCALES),
  weight: z.number().int(),
  auto_fail: z.boolean(),
  score: z.number().nullable(),
  passed: z.boolean().nullable(),
  reason: z.string().nullable(),
  evidence: z.array(z.number().int()),
  override_score: z.number().nullable(),
  override_note: z.string().nullable(),
  overridden_by_name: z.string().nullable(),
  overridden_at: z.string().nullable(),
});
export type QaScore = z.infer<typeof QaScoreSchema>;

/**
 * A supervisor's own answer to one question. Null clears it, which puts the model's answer back.
 * The model's is never overwritten -- "the model is not the judge" has to stay checkable.
 */
export const QaOverrideSchema = z.object({
  score: z.number().min(0).max(1).nullable(),
  note: z.string().max(1000).nullable().optional(),
}).strict();

/** What the technical and compliance analyses return: findings, each citing a line. */
export const FindingsResultSchema = z.object({
  findings: z.array(z.object({
    seq: z.number().int().min(0),
    severity: z.enum(['info', 'low', 'high']),
    what: z.string().max(400),
    suggestion: z.string().max(400).optional(),
  })).max(30),
});
export type FindingsResult = z.infer<typeof FindingsResultSchema>;

/** What the scorecard analysis returns: one answer per question, each citing its lines. */
export const ScorecardResultSchema = z.object({
  answers: z.array(z.object({
    seq: z.number().int().min(0),
    /** 0..1, or null for a question this call gave no occasion to answer. */
    score: z.number().min(0).max(1).nullable(),
    reason: z.string().max(400),
    evidence: z.array(z.number().int().min(0)).max(10),
  })).max(50),
});
export type ScorecardResult = z.infer<typeof ScorecardResultSchema>;
